// Poistka limitu veľkosti pred odoslaním (#162); hlavná kontrola je pri výbere súboru.
import { assertUploadSize } from '../shared/uploadLimits';
import { notifyJobsChanged } from '../shared/useAdaptivePolling';

const API_BASE_URL = process.env.REACT_APP_API_ADDRESS;

// Standard headers for JSON requests
const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("access_token")}`,
  "Content-Type": "application/json",
});

// Headers for File Uploads (Browser automatically sets Content-Type boundary for FormData)
const getUploadHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("access_token")}`,
});

// Notice the BASE_PATH now perfectly matches the Django Router registration
const BASE_PATH = `${API_BASE_URL}/anonymization/pii/async/jobs`;

// --- Dávkovanie uploadu a opakovanie pri plnej fronte (#157, 22. 9. 2026) ---
// Panely posielali celú dávku naraz cez Promise.all. Pri 50 súboroch sa fronta
// enginu (20 miest) preplnila a 16 súborov skončilo chybou 502. Semafor drží
// najviac MAX_SUBEZNE súbežných spustení, zvyšok počká v rade; keď backend
// vráti 429, súbor sa po Retry-After pošle znova.
const MAX_SUBEZNE = 5;
// Opakovanie pri plnej fronte: 12 × 5 s (1 min) nestačilo na ťažké skeny (23. 9. 2026).
// Čakanie rastie 5 → 10 → 20 → 30 s, spolu cca 15 minút, kým sa to vzdá.
const POCET_POKUSOV = 35;
const odkladPokusu = (pokus, retryAfter) =>
  Math.max(retryAfter || 0, Math.min(30, 5 * 2 ** Math.min(pokus - 1, 3)));
const HLASKA_FRONTA = "Systém pracuje, súbory sú v poradí.";

let bezi = 0;
const cakajuci = [];

const zaberSlot = () =>
  new Promise((pustDalej) => {
    if (bezi < MAX_SUBEZNE) {
      bezi += 1;
      pustDalej();
    } else {
      cakajuci.push(pustDalej);
    }
  });

const uvolniSlot = () => {
  const dalsi = cakajuci.shift();
  if (dalsi) {
    dalsi();
  } else {
    bezi = Math.max(0, bezi - 1);
  }
};

const pockaj = (ms) => new Promise((hotovo) => { setTimeout(hotovo, ms); });

/**
 * 1. Start the asynchronous anonymization process (Upload & Queue)
 * @param {File} file - The PDF file to upload
 * @param {Object} options - Granular anonymization options or profile/template IDs
 * @param {string} [externalGroupId] - Optional batch UUID
 * @returns {Promise<{job_id: number, status: string}>}
 */
// onWait(info) — nepovinné: panel ukáže, prečo súbor ešte nie je vo fronte
// ({ reason: 'slot' | 'queue_full' | 'uploading', attempt, max, retryIn }).
export const startAsyncAnonymization = async (file, options = {}, externalGroupId = null, onWait = null) => {
  assertUploadSize(file);
  const formData = new FormData();
  formData.append("file", file);
  // Používateľ potvrdil dokument v inom jazyku (predkontrola 23. 9. 2026)
  if (file?.cenzorLangConfirmed) formData.append("confirm_language", "1");
  
  const safeOptions = options && typeof options === 'object' ? options : {};

  // 🔹 Pull out profileId, defaultTemplateId and anonymizationGroupId
  const {
    profileId,
    defaultTemplateId,
    anonymizationGroupId,
    ...restOptions
  } = safeOptions;

  // Prefer the groupId from options if provided, otherwise fallback to the argument
  const finalGroupId = anonymizationGroupId || externalGroupId;

  // 🔹 Send the group id as its own field (regardless of mode)
  if (finalGroupId) {
    formData.append("anonymization_group_id", String(finalGroupId));
  }

  if (defaultTemplateId) {
    // Template mode
    formData.append("default_anonymization_profile_template_id", String(defaultTemplateId));
    // keep existing behavior: no "options" field in this branch
  } else if (profileId) {
    // Profile mode: backend will load AnonymizationProfile by id
    formData.append("anonymization_profile_id", String(profileId));
    // No "options" field when using a profile
  } else {
    // Custom options mode: send options JSON
    formData.append("options", JSON.stringify(restOptions));
  }

  // Maps to: POST /api/anonymization/pii/async/jobs/start/
  // Cez semafor, nech na engine nejde celá dávka naraz (#157).
  const oznam = (info) => { try { if (onWait) onWait(info); } catch (e) { /* UI chyba nesmie zastaviť upload */ } };
  if (bezi >= MAX_SUBEZNE) oznam({ reason: 'slot' });
  await zaberSlot();
  oznam({ reason: 'uploading' });
  try {
    for (let pokus = 1; pokus <= POCET_POKUSOV; pokus += 1) {
      // eslint-disable-next-line no-await-in-loop
      const response = await fetch(`${BASE_PATH}/start/`, {
        method: "POST",
        headers: getUploadHeaders(), // Notice we don't send 'Content-Type'
        body: formData,
      });

      if (response.ok) {
        // eslint-disable-next-line no-await-in-loop
        const vysledok = await response.json();
        notifyJobsChanged(); // História spracovania sa obnoví hneď, nie až o 60 s
        return vysledok;
      }

      // 429 = fronta enginu je plná. Nie je to porucha, počká sa a skúsi znova.
      if (response.status === 429) {
        // eslint-disable-next-line no-await-in-loop
        const data = await response.json().catch(() => ({}));
        if (pokus === POCET_POKUSOV) {
          throw new Error(data.detail || HLASKA_FRONTA);
        }
        const odklad = odkladPokusu(pokus, Number(response.headers.get("Retry-After") || data.retry_after || 0));
        oznam({ reason: 'queue_full', attempt: pokus, max: POCET_POKUSOV, retryIn: Math.max(1, odklad) });
        // eslint-disable-next-line no-await-in-loop
        await pockaj(Math.max(1, odklad) * 1000);
        oznam({ reason: 'uploading' });
        continue;
      }

      // eslint-disable-next-line no-await-in-loop
      const errorData = await response.json().catch(() => ({}));
      const chyba = new Error(errorData.detail || `Failed to start async job (status ${response.status})`);
      chyba.code = errorData.code;   // language_confirm (409) / too_many_pages (422)
      chyba.info = errorData;
      throw chyba;
    }
    throw new Error(HLASKA_FRONTA);
  } finally {
    uvolniSlot();
  }
};

/**
 * 2. List all async anonymization jobs (Also synchronizes statuses with backend)
 * @returns {Promise<Array<{id: number, input_filename: string, status: string, created_at: string, error_message: string}>>}
 */
export const getAsyncAnonymizationJobs = async () => {
  // Maps to: GET /api/anonymization/pii/async/jobs/
  const response = await fetch(`${BASE_PATH}/`, {
    method: "GET",
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `Failed to fetch jobs list (status ${response.status})`);
  }

  return await response.json();
};

/**
 * 3. Download the final anonymized file (Applies redactions just-in-time)
 * Note: Returns a Blob, which your React component can use to trigger a download
 * @param {number} jobId - The ID of the completed job
 * @returns {Promise<Blob>}
 */
export const downloadAsyncAnonymizedFile = async (jobId) => {
  // Maps to: GET /api/anonymization/pii/async/jobs/{jobId}/download/
  const response = await fetch(`${BASE_PATH}/${jobId}/download/`, {
    method: "GET",
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `Failed to download file (status ${response.status})`);
  }

  // We expect a binary file back, so we parse it as a Blob instead of JSON
  return await response.blob();
};

/**
 * 4. Delete an async anonymization job and its associated file from the database
 * @param {number} jobId - The ID of the job to delete
 * @returns {Promise<{success: boolean}>}
 */
export const deleteAsyncAnonymizationJob = async (jobId) => {
  // Maps to: DELETE /api/anonymization/pii/async/jobs/{jobId}/
  const response = await fetch(`${BASE_PATH}/${jobId}/`, {
    method: "DELETE",
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `Failed to delete job (status ${response.status})`);
  }

  // DRF's DestroyModelMixin returns 204 No Content with an empty body.
  if (response.status === 204) {
    return { success: true };
  }

  // Fallback just in case a future update changes it back to a 200 OK with JSON
  return await response.json().catch(() => ({ success: true }));
};

/**
 * 5. Get the validation report for a completed async PDF job
 * @param {number} jobId - The ID of the completed job
 * @returns {Promise<{filename: string, content_type: string, pages: number, gdpr_values_count: number, breakdown: Object, should_not_share: boolean}>}
 */
export const getAsyncValidationReport = async (jobId) => {
  // Maps to: GET /api/anonymization/pii/async/jobs/{jobId}/validation_report/
  const response = await fetch(`${BASE_PATH}/${jobId}/validation_report/`, {
    method: "GET",
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `Failed to fetch validation report (status ${response.status})`);
  }

  return await response.json();
};